import { OrderHistory, EmailProductData } from '../../stores/appOrderHistoryStore';
import { RevenueHistory } from '../../services/revenuehistory/revenueHistoryService';
import {
  CostPriceAnalysis,
  CostPriceIngredient,
  IngredientAlternative,
} from '../../services/costPrice/costPriceService';
import { Subrecipe } from '../../services/subrecipes/subrecipesService';
import { applyByoQuantityOverrides, COST_PRICE_TYPE_CHOICE } from '../costPriceUtils';
import { getProductQuantity, selectValuationLine } from '../platformUtils';
import { resolveSpendSide } from '../spendSideUtils';
import { ReportRange, inRange, productKey, pricePerStdUnit, ProductKeyFn } from './shared';
import { AllocationNeed, SubrecipeHop, SupplyMap, AllocationResult, allocateNeeds, collectOptionSets, collectPools } from './allocationEngine';
import {
  accumulateDishIngredients,
  addNeedPath,
  finalizeNeedPaths,
  scaleNeedPaths,
  NeedPath,
} from './recipeExplosion';

/**
 * Pure, React-free computation for the Product Utilization / Waste report (R2).
 * Frontend port of `buildWasteSection` (`server/services/dataExportService.ts:606-873`):
 * per ordered product, reconcile how much was ORDERED in the period vs how much
 * the menu should have NEEDED (cost-price recipes incl. subrecipes & nested
 * cost-price refs, waste % applied, weighted by units sold). The gap is "potential
 * waste", valued at the latest ordered price. Numbers match the super-admin Data
 * Export waste section for the same restaurant + range.
 *
 * Frontend-only honesty improvements over the export: products with no recipe
 * mapping and products with no `quantityInfo` are split into separate groups and
 * excluded from the headline waste KPI.
 *
 * User-LOGGED waste is deliberately NOT computed here. It is attributed per product by
 * `computeWasteByProduct` (`./wasteByProduct.ts`) — the one dish-attribution path — and
 * rendered only in the product drawer, so nothing in this file's numbers depends on it.
 *
 * Inputs are already scoped to the selected restaurant by the data-loading hooks;
 * period filtering happens here against `ReportRange`. All € on the ex-VAT basis.
 */

type RevSide = 'food' | 'drinks' | 'other';

/** One ordered product over the period (spend + base-unit quantity), keyed + sided. */
interface OrderedProduct {
  key: string;
  name: string;
  supplier: string;
  /** Raw platform + unknown-supplier name, kept so the UI can render supplier logos. */
  platform: string;
  unknownSupplierName?: string;
  /** Supplier article id (product code) shown in the table. */
  articleId?: string;
  side: RevSide;
  category?: string;
  spend: number;
  orderedBase: number; // total base units (g/ml/p) ordered in range
  stdUnit: string; // 'g' | 'ml' | 'p' | ''
  hasQuantityInfo: boolean; // false → base units uncomputable for this product
}

export interface NeededInfo {
  base: number; // total base units (g/ml/p) the menu needed in the period
  dishes: Set<string>; // cost-price item names that use this product
  subrecipes: Set<string>; // subrecipe names this product flows through (any depth)
  /** True when this product received need only as a NON-primary allocation option
   *  (an ingredient alternative) — drives the report's "alternative for …" badge. */
  receivedViaAlternative?: boolean;
  /** Primary product keys this product substitutes for (badge tooltip). */
  alternativeForKeys?: Set<string>;
  /** Subset of `dishes` whose demand reached this product as an alternative (drawer marker). */
  dishesViaAlternative?: Set<string>;
  /** Engine allocation per option-set signature (Σ = `base`) — grouped-view slicing basis. */
  allocationsBySet?: Map<string, number>;
}

/** Over-ordering status derived from a product's utilization %. */
export type UtilizationStatus = 'healthy' | 'review' | 'critical';

/** How many recent orders to link per product in the drawer. */
const RECENT_ORDERS_LIMIT = 5;

/** Per-dish recipe-side need attributed to a product — the drawer's "how was the
 *  needed quantity calculated" breakdown (per portion × portions sold = needed). */
export interface DishNeedBreakdown {
  dishName: string;
  /** Cost-price analysis owning this dish's ingredient lines — the drawer's edit target.
   *  Undefined only if the dish label couldn't be traced back to an analysis. */
  costPriceId?: string;
  /** Base units (g/ml/p) ONE portion of the dish consumes — derived as
   *  `neededBase / portions`, so it stays a true per-plate amount even when the product
   *  reaches the dish through a sub-recipe (whose ingredient rows are batch-sized). */
  perPortionBase: number;
  /** Units of the dish SOLD in the period (BYO add-on rows: picks). Never carries the
   *  sub-recipe scaling — that lives inside `neededBase`. */
  portions: number;
  /** Recipe-side needed base for this dish (Σ over its needs of portions × perPortionBase
   *  as the walk emitted them) — always equals `perPortionBase × portions`. */
  neededBase: number;
  valueEur: number | null;
  /** The distinct routes this dish reaches the product by, so the card can explain WHERE
   *  `perPortionBase` came from (Σ of their `perPortionBase` is it). Empty whenever every
   *  route is direct — i.e. exactly the dishes whose card needs no extra explanation.
   *  Optional and absent-means-empty, matching `PerDishUsage.prepPaths` on the leakage side. */
  prepPaths?: NeedPath[];
}

/** One recent order containing a product — feeds the drawer's order links. */
export interface RecentProductOrder {
  orderHistoryId: string;
  orderDate: Date;
  /** Ordered quantity on that order line (platform-aware). */
  quantity: number;
  /** Ex-VAT unit price on that order line. */
  unitPrice: number;
}

/**
 * Map a utilization % (needed / ordered × 100) to a health status. Low utilization
 * means a lot was ordered relative to what the recipes needed → over-ordering.
 * ≥90% healthy · 70–89% review · <70% critical. Null (no ordered qty) → healthy.
 */
export function getUtilizationStatus(utilizationPercent: number | null): UtilizationStatus {
  if (utilizationPercent === null || utilizationPercent >= 90) return 'healthy';
  if (utilizationPercent >= 70) return 'review';
  return 'critical';
}

/** Explain a product's allocated share only when it differs meaningfully from dish demand. */
export function poolShareOf(neededBase: number, breakdownTotal: number): number | null {
  return breakdownTotal > 0 && Math.abs(neededBase - breakdownTotal) / breakdownTotal > 0.005
    ? neededBase / breakdownTotal
    : null;
}

/**
 * The report's page-level tabs. ONE selection scopes everything below it — the KPI
 * cards, the chart, and the table — so the whole page always answers the same
 * question. The first five are subsets of the same mapped-product rows and share the
 * same layout (chart + Ordered-vs-Needed). `unmapped` is the exception: those rows
 * are a different type (spend only, no ordered/needed), so selecting it swaps the
 * whole view — spend bars + the Purchased-but-not-linked table, which appears
 * nowhere else.
 */
export type UtilizationTab = 'favorites' | 'over' | 'under' | 'all' | 'recipe' | 'unmapped';

export const UTILIZATION_TABS: UtilizationTab[] = ['favorites', 'over', 'under', 'all', 'recipe', 'unmapped'];

/** True for the tab that swaps the page to the unmapped-products view. */
export const isUnmappedTab = (tab: UtilizationTab): boolean => tab === 'unmapped';

/** A mapped product row (recipe-linked + base units computable) in the table. */
export interface UtilizationProductRow {
  key: string;
  name: string;
  supplier: string;
  /** Raw platform + unknown-supplier name for logo rendering. */
  platform: string;
  unknownSupplierName?: string;
  /** Supplier article id (product code). */
  articleId?: string;
  side: 'food' | 'drinks';
  category?: string;
  dishes: string[];
  /** Subrecipe names this product flows through (for the recipe filter). */
  subrecipes: string[];
  /** What was actually paid for this product in the period — the same `spend` that
   *  feeds `periodSpend`/`spendBySide`, exposed per row so a filtered view can show
   *  its own share of spend without re-deriving it from prices. */
  spend: number;
  stdUnit: string;
  orderedBase: number;
  neededBase: number;
  /** needed / ordered × 100, null when ordered is 0. */
  utilizationPercent: number | null;
  /** ordered − needed (base units). Positive = over-ordered. */
  wasteBase: number;
  /** Potential waste valued at the latest ordered price, null when no price data. */
  wasteEur: number | null;
  /** Latest ordered price per base unit (€/g, €/ml, €/pc), null when no price data. */
  pricePerBase: number | null;
  /** Up to 5 most recent orders (all-time, newest first) containing this product. */
  recentOrders: RecentProductOrder[];
  /** True when this row's need was pooled to it as an ingredient alternative. */
  receivedViaAlternative: boolean;
  /** Display names of the primary products this row is an alternative for (badge). */
  alternativeForNames: string[];
  /** Subset of `dishes` whose demand reached this product via alternative-allocation
   *  (the drawer marks these dish rows with the alternative icon). */
  dishesViaAlternative: string[];
  /** Recipe-side per-dish need breakdown, for the drawer's formula cards. */
  dishBreakdown: DishNeedBreakdown[];
  /** Organization totals retain local excess and shortages separately. */
  restaurantContributions?: UtilizationProductRow[];
  potentialWasteEur?: number;
  hasOverOrdering?: boolean;
  hasUnderOrdering?: boolean;
}

/** Over-ordered = more bought than the recipes needed, and we can value the surplus. */
export const isOverOrdered = (r: UtilizationProductRow): boolean =>
  r.hasOverOrdering ?? (r.wasteBase > 0 && r.wasteEur !== null && r.wasteEur > 0);

/** Under-ordered = recipes needed more than was bought (negative surplus). */
export const isUnderOrdered = (r: UtilizationProductRow): boolean => r.hasUnderOrdering ?? (r.wasteBase < 0);

/** Sum local excess without offsetting it against another restaurant's shortage. */
export const potentialWasteForRow = (r: UtilizationProductRow): number =>
  r.potentialWasteEur ?? Math.max(0, r.wasteEur ?? 0);

/** Rows feeding the per-recipe chart: over-ordered products attached to a recipe. */
export const isRecipeAttributable = (r: UtilizationProductRow): boolean =>
  potentialWasteForRow(r) > 0 && (r.dishes.length > 0 || r.subrecipes.length > 0);

/**
 * The mapped product rows a page tab is showing. `favorites` is deliberately NOT an
 * over/under subset — a starred product stays visible whatever its utilization.
 * `unmapped` has no mapped rows at all; that tab renders the unmapped view instead.
 */
export function filterRowsByTab(
  rows: UtilizationProductRow[],
  tab: UtilizationTab,
  favoriteKeys: string[]
): UtilizationProductRow[] {
  switch (tab) {
    case 'unmapped':
      return [];
    case 'favorites':
      return rows.filter((r) => favoriteKeys.includes(r.key));
    case 'over':
      return rows.filter(isOverOrdered);
    case 'under':
      return rows.filter(isUnderOrdered);
    case 'recipe':
      return rows.filter(isRecipeAttributable);
    default:
      return rows;
  }
}

// --- grouped (alternative-pool) view --------------------------------------

/** One product's line inside an alternative group. In the dish-grouped view the
 *  numbers are the engine-allocated need from this option set plus the matching
 *  proportional slice of the product's ordered volume; in the pooled view they are
 *  the product's full flat-row numbers. Configured members that were NOT purchased
 *  this period are included with `orderedBase` 0 and no `row`, so the group always
 *  lists its full composition. */
export interface UtilizationGroupMember {
  key: string;
  name: string;
  platform: string;
  unknownSupplierName?: string;
  supplier: string;
  articleId?: string;
  category?: string;
  stdUnit: string;
  isPrimary: boolean;
  orderedBase: number;
  neededBase: number;
  wasteBase: number;
  wasteEur: number | null;
  utilizationPercent: number | null;
  /** Flat-tab row when the product was purchased this period (drawer link). */
  row?: UtilizationProductRow;
}

/** One alternative group: a distinct primary+alternatives option set, merged across
 *  dishes when the set is identical (main/alternative swaps included). Base-unit
 *  aggregates are null when members mix std units — only € stays comparable. */
export interface UtilizationGroupRow {
  signature: string;
  /** Group label anchor: the primary product's display name. */
  primaryName: string;
  /** Configured set size (primary + alternatives) — can exceed `members.length` when
   *  a set product wasn't purchased in the period (no flat row to slice). */
  configuredCount: number;
  side: 'food' | 'drinks';
  dishes: string[];
  subrecipes: string[];
  /** Shared std unit of all members, or null when mixed. */
  stdUnit: string | null;
  orderedBase: number | null;
  neededBase: number | null;
  utilizationPercent: number | null;
  wasteBase: number | null;
  wasteEur: number | null;
  members: UtilizationGroupMember[];
}

/**
 * The report's shared filter selection. One instance lives on the report page and
 * scopes the KPIs, the chart, the Ordered-vs-Needed table and the donuts together —
 * previously the chart and the table each kept their own unsynced copy.
 */
export interface UtilizationFilters {
  /** Matches product name or article id. */
  search: string;
  /** Empty = no restriction (all), for each of these. */
  suppliers: string[];
  categories: string[];
  statuses: string[];
  /** 'all', `dish:<name>` or `sub:<name>`. */
  recipe: string;
}

export const EMPTY_UTILIZATION_FILTERS: UtilizationFilters = {
  search: '',
  suppliers: [],
  categories: [],
  statuses: [],
  recipe: 'all',
};

export const hasActiveUtilizationFilters = (f: UtilizationFilters): boolean =>
  f.search.trim() !== '' || f.suppliers.length > 0 || f.categories.length > 0 || f.statuses.length > 0 || f.recipe !== 'all';

/** Apply the shared filters to mapped product rows. Tab filtering is separate. */
export function applyUtilizationFilters(
  rows: UtilizationProductRow[],
  f: UtilizationFilters
): UtilizationProductRow[] {
  const q = f.search.trim().toLowerCase();
  return rows.filter((r) => {
    if (q !== '' && !r.name.toLowerCase().includes(q) && !(r.articleId ?? '').toLowerCase().includes(q)) return false;
    if (f.suppliers.length > 0 && !f.suppliers.includes(r.supplier)) return false;
    const contributions = r.restaurantContributions ?? [r];
    if (f.categories.length > 0 && !contributions.some(local => local.category && f.categories.includes(local.category))) return false;
    if (f.statuses.length > 0 && !contributions.some(local => f.statuses.includes(getUtilizationStatus(local.utilizationPercent)))) return false;
    if (f.recipe.startsWith('dish:') && !r.dishes.includes(f.recipe.slice(5))) return false;
    if (f.recipe.startsWith('sub:') && !r.subrecipes.includes(f.recipe.slice(4))) return false;
    return true;
  });
}

/** Unmapped twin of `applyUtilizationFilters`. Only the filters that mean something
 *  for a spend-only row apply — status and recipe have no counterpart here, and the
 *  controls hide them on that tab rather than silently ignoring them. */
export function applyUnmappedFilters(
  rows: UnmappedProductRow[],
  f: UtilizationFilters
): UnmappedProductRow[] {
  const q = f.search.trim().toLowerCase();
  return rows.filter((r) => {
    if (q !== '' && !r.name.toLowerCase().includes(q) && !(r.articleId ?? '').toLowerCase().includes(q)) return false;
    if (f.suppliers.length > 0 && !f.suppliers.includes(r.supplier)) return false;
    if (f.categories.length > 0 && !(r.category && f.categories.includes(r.category))) return false;
    return true;
  });
}

/** Grouped twin of `applyUtilizationFilters`: a group matches when any member does
 *  (its own name/aggregate status count too, since the group row is what's shown). */
export function applyUtilizationFiltersToGroups(
  groups: UtilizationGroupRow[],
  f: UtilizationFilters
): UtilizationGroupRow[] {
  const q = f.search.trim().toLowerCase();
  return groups.filter((g) => {
    if (
      q !== '' &&
      !g.primaryName.toLowerCase().includes(q) &&
      !g.members.some((m) => m.name.toLowerCase().includes(q) || (m.articleId ?? '').toLowerCase().includes(q))
    ) {
      return false;
    }
    if (f.suppliers.length > 0 && !g.members.some((m) => f.suppliers.includes(m.supplier))) return false;
    if (f.categories.length > 0 && !g.members.some((m) => m.category && f.categories.includes(m.category))) return false;
    if (f.statuses.length > 0 && !f.statuses.includes(getUtilizationStatus(g.utilizationPercent))) return false;
    if (f.recipe.startsWith('dish:') && !g.dishes.includes(f.recipe.slice(5))) return false;
    if (f.recipe.startsWith('sub:') && !g.subrecipes.includes(f.recipe.slice(4))) return false;
    return true;
  });
}

/** Grouped-tab twin of `filterRowsByTab` + the page's side filter, applied to
 *  group rows on their own aggregates. The grouped tabs show GROUPS ONLY — products
 *  without alternatives live on the Products tab. */
export function filterGroupedView(
  groups: UtilizationGroupRow[],
  activeSides: Array<'food' | 'drinks'>,
  tab: UtilizationTab,
  favoriteKeys: string[]
): UtilizationGroupRow[] {
  const groupTab = (g: UtilizationGroupRow): boolean => {
    switch (tab) {
      case 'unmapped':
        return false;
      case 'favorites':
        // A group is a favourite as soon as ANY of its members is starred — the
        // group row is the aggregate the user starred a product inside of.
        return g.members.some((m) => favoriteKeys.includes(m.key));
      case 'over':
        return g.wasteEur !== null && g.wasteEur > 0;
      case 'under':
        // Mixed-unit groups have no base-unit sum — fall back to € so a net
        // under-ordered group still surfaces in the Under tab.
        return g.wasteBase !== null ? g.wasteBase < 0 : g.wasteEur !== null && g.wasteEur < 0;
      case 'recipe':
        return g.wasteEur !== null && g.wasteEur > 0 && (g.dishes.length > 0 || g.subrecipes.length > 0);
      default:
        return true;
    }
  };
  return groups.filter((g) => activeSides.includes(g.side) && groupTab(g));
}

/** Collapsed group of products excluded from the headline waste KPI. */
export interface UtilizationGroup {
  count: number;
  spend: number;
}

/** One ordered-but-unmapped product (no recipe links it). Feeds the
 *  "Purchased but not linked" table + the chart's Unmapped tab. */
export interface UnmappedProductRow {
  key: string;
  name: string;
  supplier: string;
  platform: string;
  unknownSupplierName?: string;
  articleId?: string;
  side: 'food' | 'drinks';
  category?: string;
  spend: number;
}

export interface UtilizationReportData {
  /** ≥1 non-CHOICE cost price linked to a menu item — else the body shows an explainer. */
  hasMappableCostPrices: boolean;
  /** Mapped rows (both sides), sorted by waste € desc. */
  rows: UtilizationProductRow[];
  /** Dish-grouped-tab groups: option-set groups with sliced member numbers. */
  grouped: UtilizationGroupRow[];
  /** Pooled-tab groups: transitive alternative pools (side-split), full member numbers. */
  pooled: UtilizationGroupRow[];
  unmappedFood: UtilizationGroup;
  unmappedDrinks: UtilizationGroup;
  /** Per-product detail behind the unmapped groups (both sides), sorted by spend desc. */
  unmappedRows: UnmappedProductRow[];
  noQuantityInfoFood: UtilizationGroup;
  noQuantityInfoDrinks: UtilizationGroup;
  /** Σ positive potential waste € across mapped rows (over-ordering only). */
  totalPotentialWasteEur: number;
  /** Total product spend in the period (all sides), the waste-% denominator. */
  periodSpend: number;
  /** Period product spend split by side (food/drinks only), for side-scoped KPIs. */
  spendBySide: { food: number; drinks: number };
  wastePercentOfSpend: number;
  overOrderedCount: number;
  suppliers: string[];
  categories: string[];
  /** Distinct dish (revenue item) names mapped in the report, for the recipe filter. */
  dishNames: string[];
  /** Distinct subrecipe names mapped in the report, for the recipe filter. */
  subrecipeNames: string[];
  comparisonHidden: boolean;
}

export interface UtilizationReportInputs {
  /** All-time order history (period-filtered + used unfiltered for latest price). */
  orders: OrderHistory[];
  /** All-time revenue history (period-filtered here). */
  revenue: RevenueHistory[];
  /** Raw cost prices (for the needed-index walk). */
  costPriceList: CostPriceAnalysis[];
  subrecipesById: Map<string, Subrecipe>;
  defaultWastePercentage: number;
  currentRestaurantId: string;
  range: ReportRange;
}

// --- ordered products -----------------------------------------------------

/** Supplier display label: any_invoice / unknown → unknownSupplierName, else platform. */
function supplierLabel(order: OrderHistory): string {
  const platform = (order.platform || '').trim();
  const lower = platform.toLowerCase();
  if (!platform || lower === 'unknown' || lower === 'any_invoice') {
    return (order.unknownSupplierName || '').trim() || platform || 'Unknown supplier';
  }
  return platform;
}

/** Per-product ordered totals (spend + base-unit quantity) over the period. */
function aggregateOrderedProducts(orders: OrderHistory[]): Map<string, OrderedProduct> {
  const map = new Map<string, OrderedProduct>();
  for (const o of orders) {
    const supplier = supplierLabel(o);
    const section = o.restaurantSection?.restaurantSectionType;
    const combined = o.restaurantSection?.combinedOrderingAccount === true;
    for (const p of o.productData) {
      const key = productKey(o.platform, p.articleId, p.name);
      const qty = getProductQuantity(p, o.platform);
      const spend = (p.price ?? 0) * qty;
      const stdAmount = p.quantityInfo?.stdAmount ?? 0;
      const stdMult = p.quantityInfo?.stdMultiplier ?? 0;
      const hasQty = !!(stdAmount && stdMult);
      const base = hasQty ? qty * stdAmount * stdMult : 0;
      // `RevSide` has no 'non-food': non-food and "neither side" both land on 'other', as before.
      const spendSide = resolveSpendSide(p, section, combined);
      const side: RevSide = spendSide === 'food' || spendSide === 'drinks' ? spendSide : 'other';
      const existing = map.get(key);
      if (existing) {
        existing.spend += spend;
        existing.orderedBase += base;
        if (hasQty) existing.hasQuantityInfo = true;
        if (!existing.stdUnit && p.quantityInfo?.stdUnit) existing.stdUnit = p.quantityInfo.stdUnit;
        if (!existing.category && p.category) existing.category = p.category;
      } else {
        map.set(key, {
          key,
          name: p.name,
          supplier,
          platform: o.platform,
          unknownSupplierName: o.unknownSupplierName,
          articleId: p.articleId,
          side,
          category: p.category,
          spend,
          orderedBase: base,
          stdUnit: p.quantityInfo?.stdUnit ?? '',
          hasQuantityInfo: hasQty,
        });
      }
    }
  }
  return map;
}

// --- needed index ---------------------------------------------------------

/** Build the per-product needed-base index across all dishes with >0 sales in the
 *  period (incl. BYO choice picks). Exported for unit testing the walker.
 *  `keyOf` decides the grouping: the default `productKey` (report aggregation, splits
 *  reused article ids) — inventory callers pass `inventoryKey` so the index is keyed
 *  by the raw `${identifier}-${platform}` inventory key and needs no string re-keying. */
/** Recipe-side per-dish need for one product key — the drawer's
 *  `perPortionBase × portions = neededBase` cards.
 *
 *  Needs are collected PRE-allocation, so a product that sits in an alternative pool
 *  appears as an option on dishes whose demand the engine ultimately gave to a sibling.
 *  `allocatedDishes` (the row's post-allocation dish set) filters those out, so the
 *  breakdown only explains demand this product actually served.
 *
 *  A need's `portions` is units-sold ALREADY multiplied by the sub-recipe/nested chain,
 *  and its `perPortionBase` is whatever scale that ingredient row was written in (a prep
 *  row is batch-sized). Neither is presentable on its own, so the card takes units sold
 *  from `unitsSoldByDish` (the walk's untouched root multiplier) and derives per-portion
 *  back out of the total — which also keeps the two factors consistent when one dish
 *  reaches the product at several scales at once (direct AND through a prep). */
function buildProductDishBreakdown(
  productKey: string,
  needs: AllocationNeed[],
  pricePerBase: number | null,
  allocatedDishes: Set<string>,
  analysisIdByDish: Map<string, string>,
  unitsSoldByDish: Map<string, number>
): DishNeedBreakdown[] {
  const byDish = new Map<string, { portions: number; needed: number; paths: Map<string, NeedPath> }>();
  for (const need of needs) {
    if (!allocatedDishes.has(need.dishName)) continue;
    const opt = need.options.find((o) => o.key === productKey);
    if (!opt) continue;
    const needed = need.portions * opt.perPortionBase;
    const cur = byDish.get(need.dishName);
    if (cur) {
      cur.portions += need.portions;
      cur.needed += needed;
    } else {
      byDish.set(need.dishName, { portions: need.portions, needed, paths: new Map() });
    }
    // Route detail for the card's prep lines. Accumulated as PERIOD TOTALS here (the same
    // `needed` the card totals), then divided by units sold below — the one place that
    // knows the divisor, exactly as `perPortionBase` itself is derived.
    addNeedPath(byDish.get(need.dishName)!.paths, need.subrecipeHops, needed);
  }
  return Array.from(byDish.entries())
    .map(([dishName, { portions, needed, paths }]) => {
      // Fall back to the raw multiplier only if the dish label never made it into the
      // sales map (it always does — a dish with no sales emits no needs at all).
      const unitsSold = unitsSoldByDish.get(dishName) ?? portions;
      return {
        dishName,
        costPriceId: analysisIdByDish.get(dishName),
        perPortionBase: unitsSold > 0 ? needed / unitsSold : 0,
        portions: unitsSold,
        neededBase: needed,
        valueEur: pricePerBase !== null ? needed * pricePerBase : null,
        prepPaths: scaleNeedPaths(finalizeNeedPaths(paths), unitsSold),
      };
    })
    .sort((a, b) => b.neededBase - a.neededBase);
}

/** Convert the allocation engine's per-key result into the `NeededInfo` shape the
 *  report row math consumes (`neededBase` → `base`, plus the alternative-badge flags). */
function allocatedToNeededInfo(alloc: AllocationResult): Map<string, NeededInfo> {
  const idx = new Map<string, NeededInfo>();
  for (const [key, a] of Array.from(alloc.entries())) {
    idx.set(key, {
      base: a.neededBase,
      dishes: a.dishes,
      subrecipes: a.subrecipes,
      receivedViaAlternative: a.receivedViaAlternative,
      alternativeForKeys: a.alternativeForKeys,
      dishesViaAlternative: a.dishesViaAlternative,
      allocationsBySet: a.allocationsBySet,
    });
  }
  return idx;
}

/** The `quantityInfo` snapshot an ingredient row carries — `stdAmount`/`stdMultiplier`
 *  are optional here because a legacy row may hold the unit alone. */
interface NeedQuantityInfo {
  stdUnit: string;
  stdAmount?: number;
  stdMultiplier?: number;
}

/** Display data of an option product harvested from its cost-price/subrecipe
 *  ingredient row — enough to render it in the report when it has NO flat row
 *  (not purchased this period). */
export interface NeedProductInfo {
  name: string;
  stdUnit: string;
  /**
   * Pack facts from the SAME snapshot `stdUnit` came from: `stdAmount` = base units per
   * piece, `stdMultiplier` = pieces per ordered unit. They are the only bridge between a
   * recipe quantity in g/ml and a count frozen in pieces (`neededQtyForCount`), so they
   * must travel with the unit rather than being re-read off a live line that may since
   * have been parsed in a different space.
   */
  stdAmount?: number;
  stdMultiplier?: number;
  platform: string;
  articleId?: string;
  customPlatformName?: string;
}

/**
 * Product display info harvested from the cost-price + subrecipe ingredient rows
 * (primary and each alternative). Used to name OPTION products with zero period
 * purchases — e.g. a primary whose demand all landed on its alternative — and to
 * render configured-but-unpurchased alternatives as group members. Flat scan (no
 * recursive walk needed); keyed by `keyOf`, defaulting to the report's `productKey`
 * (the Leakage engine passes `inventoryKey` so the infos line up with count items).
 */
export function collectNeedProductInfo(
  costPriceList: CostPriceAnalysis[],
  subrecipesById: Map<string, Subrecipe>,
  keyOf: ProductKeyFn = productKey
): Map<string, NeedProductInfo> {
  const infos = new Map<string, NeedProductInfo>();
  const add = (row?: { platform?: string; articleId?: string; productName?: string; customPlatformName?: string; quantityInfo?: NeedQuantityInfo }) => {
    if (!row?.platform || row.productName === undefined) return;
    const key = keyOf(row.platform, row.articleId, row.productName);
    if (key && !infos.has(key)) {
      infos.set(key, {
        name: row.productName,
        stdUnit: row.quantityInfo?.stdUnit ?? '',
        stdAmount: row.quantityInfo?.stdAmount,
        stdMultiplier: row.quantityInfo?.stdMultiplier,
        platform: row.platform,
        articleId: row.articleId,
        customPlatformName: row.customPlatformName,
      });
    }
  };
  const scan = (ings: Array<{ platform?: string; articleId?: string; productName?: string; customPlatformName?: string; quantityInfo?: NeedQuantityInfo; alternatives?: IngredientAlternative[] }>) => {
    for (const ing of ings) {
      add(ing);
      for (const alt of ing.alternatives ?? []) add(alt);
    }
  };
  for (const cpa of costPriceList) scan(cpa.ingredients as any);
  for (const sub of Array.from(subrecipesById.values())) scan(sub.ingredients as any);
  return infos;
}

/**
 * One analysis per revenue item — the winner for each `menuItemName`, keyed by the EXACT
 * name. TWIN of `dataExportService.ts`'s copy; keep both identical.
 *
 * Exact, not normalized: the sales walk below charges `salesByMenuItem.get(cpa.menuItemName)`,
 * which is keyed on the raw revenue item name. Two analyses whose names differ only in casing
 * therefore consume DIFFERENT revenue rows and cannot double-count — folding them together
 * here would silently drop one of them (a partner venue exports the restaurant's own dishes
 * in its own casing, so this is a real pair, not a duplicate).
 *
 * Every needs walk below charges a dish's FULL units-sold to each analysis linked to
 * it, so a revenue item owned by two analyses doubles its theoretical usage — and with
 * it leakage, expected stock and waste needs. The server rejects a second link
 * (`CostPriceDuplicateMenuItemError`), but restaurants carry historical duplicates from
 * before that guard, so every consumer applies this defensively on read.
 *
 * This is a stopgap, not a resolution: when two analyses hold genuinely different
 * recipes no automatic pick is right. It stops the number being wrong by a factor of N
 * until the duplicate is merged by hand.
 *
 * Winner: an analysis WITH ingredients beats an empty stub (a stub contributes no needs
 * and would silently zero out a real recipe), then most recently updated, then highest
 * id. Fully deterministic — a report must not flip between renders.
 *
 * CHOICE rows are excluded: shared per-pick costs, not sold menu items, and every
 * caller below skips them anyway.
 */
export function winningDishAnalyses(costPriceList: CostPriceAnalysis[]): Map<string, CostPriceAnalysis> {
  const beats = (a: CostPriceAnalysis, b: CostPriceAnalysis): boolean => {
    const rank = (x: CostPriceAnalysis) => ((x.ingredients?.length ?? 0) > 0 ? 1 : 0);
    if (rank(a) !== rank(b)) return rank(a) > rank(b);
    const at = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const bt = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    if (at !== bt) return at > bt;
    return a.id > b.id;
  };

  const winners = new Map<string, CostPriceAnalysis>();
  for (const cpa of costPriceList) {
    if (cpa.costPriceType === COST_PRICE_TYPE_CHOICE || !cpa.menuItemName) continue;
    const key = cpa.menuItemName;
    const current = winners.get(key);
    if (!current || beats(cpa, current)) winners.set(key, cpa);
  }
  return winners;
}

/** Ids of the analyses `winningDishAnalyses` picked — the dish loops' skip test. */
export function winningDishAnalysisIds(costPriceList: CostPriceAnalysis[]): Set<string> {
  return new Set(Array.from(winningDishAnalyses(costPriceList).values()).map((a) => a.id));
}

export function buildNeededIndex(
  costPriceList: CostPriceAnalysis[],
  revenue: RevenueHistory[],
  subrecipesById: Map<string, Subrecipe>,
  defaultWaste: number,
  restId: string,
  keyOf: ProductKeyFn = productKey,
  supply?: SupplyMap,
  /** When provided, receives the collected allocation needs (grouped-view metadata). */
  needsOut?: AllocationNeed[],
  /** When provided, receives `dishName` → the cost-price analysis id that owns that
   *  dish's ingredient lines — the drawer's "edit this dish" target. Filled here
   *  because this is the only place both the label and its analysis are in hand. */
  dishAnalysisIdOut?: Map<string, string>,
  /** When provided, receives `dishName` → units SOLD in the period (BYO add-on labels:
   *  picks) — the walk's root multiplier before any sub-recipe rescaling, which the
   *  emitted needs no longer carry separately. Same reason as above: only here are the
   *  label and its sales count both in hand. Accumulated, so two analyses that share a
   *  `costPriceName` (and therefore one dish label) sum rather than clobber. */
  dishUnitsSoldOut?: Map<string, number>
): Map<string, NeededInfo> {
  // Units sold per menu item in the period, plus BYO choice picks per menu item.
  const salesByMenuItem = new Map<string, number>();
  const choiceSalesByMenuItem = new Map<string, Map<string, number>>();
  for (const r of revenue) {
    for (const cat of r.revenueData.categories) {
      for (const item of cat.items) {
        salesByMenuItem.set(item.name, (salesByMenuItem.get(item.name) ?? 0) + (item.quantity || 0));
        for (const choice of item.choices ?? []) {
          let picks = choiceSalesByMenuItem.get(item.name);
          if (!picks) {
            picks = new Map<string, number>();
            choiceSalesByMenuItem.set(item.name, picks);
          }
          picks.set(choice.name, (picks.get(choice.name) ?? 0) + (choice.quantity || 0));
        }
      }
    }
  }

  // cpById stays the FULL list — a nested cost-price reference must still resolve to a
  // deduped-away analysis, it just doesn't get charged sales of its own.
  const cpById = new Map(costPriceList.map((c) => [c.id, c]));
  const dishWinners = winningDishAnalyses(costPriceList);
  const dishWinnerIds = new Set(Array.from(dishWinners.values()).map((a) => a.id));
  const needs: AllocationNeed[] = [];
  for (const cpa of costPriceList) {
    if (cpa.costPriceType === COST_PRICE_TYPE_CHOICE) continue;
    if (!cpa.menuItemName) continue;
    if (!dishWinnerIds.has(cpa.id)) continue; // duplicate link — charging both doubles the need
    const unitsSold = salesByMenuItem.get(cpa.menuItemName) ?? 0;
    if (unitsSold <= 0) continue;
    dishAnalysisIdOut?.set(cpa.costPriceName, cpa.id);
    dishUnitsSoldOut?.set(cpa.costPriceName, (dishUnitsSoldOut.get(cpa.costPriceName) ?? 0) + unitsSold);
    accumulateDishIngredients(
      cpa.ingredients,
      unitsSold,
      cpa.costPriceName,
      subrecipesById,
      defaultWaste,
      restId,
      needs,
      cpById,
      new Set(),
      new Set(),
      [],
      0,
      keyOf
    );
  }

  // BYO choice picks consume the shared CHOICE analysis's ingredients (with the
  // parent dish's per-choice quantity overrides applied).
  const choiceByName = new Map(
    costPriceList.filter((c) => c.costPriceType === COST_PRICE_TYPE_CHOICE).map((c) => [c.costPriceName, c])
  );
  // Same winner as the dish loop, so a BYO parent's overrides come from the analysis
  // that actually got charged (this used to be "last one wins" on a duplicate link).
  const dishByMenuItemName = dishWinners;
  for (const [menuItemName, picks] of Array.from(choiceSalesByMenuItem.entries())) {
    // Winners are keyed on the exact menu_item_name, which is what the raw revenue name is.
    const parentDish = dishByMenuItemName.get(menuItemName);
    for (const [choiceName, qtyPicked] of Array.from(picks.entries())) {
      if (qtyPicked <= 0) continue;
      const choice = choiceByName.get(choiceName);
      if (!choice) continue; // no CHOICE cost price yet — same partial-cost rule as BYO dish cost
      const ingredients = applyByoQuantityOverrides(choice.ingredients, parentDish?.byoChoiceOverrides?.[choiceName]);
      const dishLabel = `${parentDish?.costPriceName || menuItemName} — ${choiceName} (add-on)`;
      // The CHOICE analysis owns these ingredient lines, so that's what "edit" opens.
      dishAnalysisIdOut?.set(dishLabel, choice.id);
      dishUnitsSoldOut?.set(dishLabel, (dishUnitsSoldOut.get(dishLabel) ?? 0) + qtyPicked);
      accumulateDishIngredients(
        ingredients,
        qtyPicked,
        dishLabel,
        subrecipesById,
        defaultWaste,
        restId,
        needs,
        cpById,
        new Set(),
        new Set([choice.id]),
        [],
        0,
        keyOf
      );
    }
  }

  // Pool primary + alternatives against the period supply (when the caller has one).
  // With no alternatives anywhere the engine short-circuits to today's exact math, so
  // results are bit-identical; callers without a supply side (per-unit needs) pass none
  // → every leaf falls back to its primary option (status quo).
  if (needsOut) needsOut.push(...needs);
  return allocatedToNeededInfo(allocateNeeds(needs, supply ?? new Map()));
}

// --- per-unit needs (all-restaurants dashboard waste) ---------------------
//
// The all-restaurants waste card can't load every restaurant's cost-price recipes,
// so the server ships a PERIOD-INDEPENDENT per-unit needs index (`buildPerUnitNeeds`
// in `server/services/dataExportService.ts`) and we compute waste here from the orders
// + revenue already in the browser. `computeWasteTotalsFromNeeds` reuses the exact same
// ordered-aggregation + valuation as `computeUtilizationReport`, so the headline numbers
// match the single-restaurant Utilization Report (pinned by a parity test).

/** One interchangeable supply option of a leaf need. `base` is the PER-PORTION base
 *  (g/ml/p) in this option's OWN units; the primary is options[0]. */
export interface PerUnitNeedOption {
  key: string; // keyOf(platform, articleId, productName) — productKey for waste, inventoryKey for leakage
  base: number; // per-portion base units (g/ml/p) for this option
}

/**
 * One leaf-ingredient need, PER 1 unit sold / 1 choice pick. Carries the full ordered
 * option list (primary + alternatives) so the waste path can pool them against real
 * supply; consumers without a purchase context (Leakage) read the primary (options[0])
 * only. `portions` = the subrecipe/nested multiplier chain (1 for a direct ingredient),
 * kept separate from the per-portion option bases so sales-weighting is `portions × sold`.
 */
export interface PerUnitNeed {
  portions: number;
  options: PerUnitNeedOption[];
  /** The prep hops behind `portions`, for the Leakage drawer's prep breakdown. Present
   *  only on a locally-built index: this shape is also a SERVER contract, and the
   *  server's producer (all-restaurants waste card, totals only) has no drawer to feed.
   *  Consumers must therefore treat it as optional, never assume it. */
  hops?: SubrecipeHop[];
}

/** Server contract: dish → per-unit needs, choice → per-pick needs (default + override). */
export interface PerUnitNeedsIndex {
  dishes: Array<{ menuItemName: string; needs: PerUnitNeed[] }>;
  choiceDefaults: Array<{ choiceName: string; needs: PerUnitNeed[] }>;
  choiceOverrides: Array<{ menuItemName: string; choiceName: string; needs: PerUnitNeed[] }>;
}

export interface WasteTotals {
  /** Σ positive potential-waste € across mapped products (over-ordering only). */
  totalPotentialWasteEur: number;
  /** Total product spend in the period (all sides) — the waste-% denominator. */
  periodSpend: number;
  /** totalPotentialWasteEur / periodSpend (0..1 ratio; 0 when spend is 0). */
  wastePercent: number;
}

/**
 * Flatten cost-price recipes into period-independent per-unit needs — the FRONTEND twin
 * of the server's `buildPerUnitNeeds` (`server/services/dataExportService.ts`). The
 * server produces this for the all-restaurants card; this twin lets the parity test
 * drive `computeWasteTotalsFromNeeds` from the same cost data `computeUtilizationReport`
 * uses, proving the two paths agree. Mirrors `buildNeededIndex`'s walk with the sales
 * multiplier factored out.
 */
export function buildPerUnitNeeds(
  costPriceList: CostPriceAnalysis[],
  subrecipesById: Map<string, Subrecipe>,
  defaultWaste: number,
  restId: string,
  keyOf: ProductKeyFn = productKey
): PerUnitNeedsIndex {
  const cpById = new Map(costPriceList.map((c) => [c.id, c]));
  const walkUnit = (ingredients: CostPriceIngredient[], seedVisitedCp?: Set<string>): PerUnitNeed[] => {
    const needs: AllocationNeed[] = [];
    accumulateDishIngredients(
      ingredients, 1, '_', subrecipesById, defaultWaste, restId, needs, cpById,
      new Set(), seedVisitedCp ?? new Set(), [], 0, keyOf
    );
    // Keep each leaf need's option list intact (no collapse, no allocation yet): the
    // period allocation runs later against real supply in computeWasteTotalsFromNeeds.
    // Consumers without purchase context read options[0] (primary), matching status quo.
    return needs.map((n) => ({
      portions: n.portions,
      options: n.options.map((o) => ({ key: o.key, base: o.perPortionBase })),
      hops: n.subrecipeHops,
    }));
  };

  // One dish entry per revenue item — a second entry with the same menuItemName would
  // be weighted by the SAME units sold downstream and double the need.
  const dishWinnerIds = winningDishAnalysisIds(costPriceList);
  const dishes: PerUnitNeedsIndex['dishes'] = [];
  for (const cpa of costPriceList) {
    if (cpa.costPriceType === COST_PRICE_TYPE_CHOICE || !cpa.menuItemName) continue;
    if (!dishWinnerIds.has(cpa.id)) continue;
    dishes.push({ menuItemName: cpa.menuItemName, needs: walkUnit(cpa.ingredients) });
  }

  const choiceList = costPriceList.filter((c) => c.costPriceType === COST_PRICE_TYPE_CHOICE);
  const choiceByName = new Map(choiceList.map((c) => [c.costPriceName, c]));
  const choiceDefaults: PerUnitNeedsIndex['choiceDefaults'] = choiceList.map((choice) => ({
    choiceName: choice.costPriceName,
    needs: walkUnit(choice.ingredients, new Set([choice.id])),
  }));

  const choiceOverrides: PerUnitNeedsIndex['choiceOverrides'] = [];
  for (const cpa of costPriceList) {
    if (cpa.costPriceType === COST_PRICE_TYPE_CHOICE || !cpa.menuItemName || !cpa.byoChoiceOverrides) continue;
    if (!dishWinnerIds.has(cpa.id)) continue; // overrides follow the charged analysis
    for (const choiceName of Object.keys(cpa.byoChoiceOverrides)) {
      const choice = choiceByName.get(choiceName);
      if (!choice) continue;
      const ingredients = applyByoQuantityOverrides(choice.ingredients, cpa.byoChoiceOverrides[choiceName]);
      choiceOverrides.push({ menuItemName: cpa.menuItemName, choiceName, needs: walkUnit(ingredients, new Set([choice.id])) });
    }
  }

  return { dishes, choiceDefaults, choiceOverrides };
}

/**
 * Extract units-sold per menu item + per-choice picks from a period's revenue.
 * Shared, zero-behaviour-change extraction used by `weightNeedsBySales` and by the
 * Leakage engine's `buildPerDishUsage` (`inventoryVariance.ts`) so both weight the
 * exact same sales the same way `buildNeededIndex` does.
 */
export function extractSalesFromRevenue(revenue: RevenueHistory[]): {
  salesByMenuItem: Map<string, number>;
  choiceSalesByMenuItem: Map<string, Map<string, number>>;
} {
  const salesByMenuItem = new Map<string, number>();
  const choiceSalesByMenuItem = new Map<string, Map<string, number>>();
  for (const r of revenue) {
    for (const cat of r.revenueData.categories) {
      for (const item of cat.items) {
        salesByMenuItem.set(item.name, (salesByMenuItem.get(item.name) ?? 0) + (item.quantity || 0));
        for (const choice of item.choices ?? []) {
          let picks = choiceSalesByMenuItem.get(item.name);
          if (!picks) {
            picks = new Map<string, number>();
            choiceSalesByMenuItem.set(item.name, picks);
          }
          picks.set(choice.name, (picks.get(choice.name) ?? 0) + (choice.quantity || 0));
        }
      }
    }
  }
  return { salesByMenuItem, choiceSalesByMenuItem };
}

/**
 * Weight a server per-unit needs index by a period's sales → one allocation `need` per
 * leaf line (portions scaled by units sold), carrying its option list. Same sales
 * extraction + skip-zero-sales rule as `buildNeededIndex`, so feeding these to
 * `allocateNeeds` reproduces exactly what the canonical report builds for the period.
 * Zero-sales dishes/choices contribute nothing (never mark a product "mapped").
 */
function weightNeedsBySales(perUnit: PerUnitNeedsIndex, periodRevenue: RevenueHistory[]): AllocationNeed[] {
  const { salesByMenuItem, choiceSalesByMenuItem } = extractSalesFromRevenue(periodRevenue);

  const out: AllocationNeed[] = [];
  const add = (needs: PerUnitNeed[], mult: number) => {
    for (const n of needs) {
      if (n.options.length === 0) continue;
      out.push({
        portions: n.portions * mult,
        options: n.options.map((o) => ({ key: o.key, perPortionBase: o.base })),
        dishName: '',
        subrecipes: [],
      });
    }
  };

  for (const d of perUnit.dishes) {
    const unitsSold = salesByMenuItem.get(d.menuItemName) ?? 0;
    if (unitsSold > 0) add(d.needs, unitsSold);
  }

  const overrideByPair = new Map(perUnit.choiceOverrides.map((o) => [`${o.menuItemName}\u0000${o.choiceName}`, o.needs]));
  const defaultByChoice = new Map(perUnit.choiceDefaults.map((c) => [c.choiceName, c.needs]));
  for (const [menuItemName, picks] of Array.from(choiceSalesByMenuItem.entries())) {
    for (const [choiceName, qtyPicked] of Array.from(picks.entries())) {
      if (qtyPicked <= 0) continue;
      const needs = overrideByPair.get(`${menuItemName}\u0000${choiceName}`) ?? defaultByChoice.get(choiceName);
      if (needs) add(needs, qtyPicked);
    }
  }

  return out;
}

/**
 * Waste totals for ONE restaurant + period, from a server per-unit needs index + that
 * restaurant's already-loaded full order/revenue history. Mirrors the headline figures
 * of `computeUtilizationReport` (parity-tested) without needing the cost-price recipes.
 */
export function computeWasteTotalsFromNeeds(args: {
  perUnit: PerUnitNeedsIndex;
  orders: OrderHistory[]; // this restaurant's full order history
  revenue: RevenueHistory[]; // this restaurant's full revenue history
  range: ReportRange;
}): WasteTotals {
  const periodOrders = args.orders.filter((o) => inRange(new Date(o.orderDate), args.range));
  const periodRevenue = args.revenue.filter((r) => inRange(new Date(r.date), args.range));

  const ordered = aggregateOrderedProducts(periodOrders);
  // Pool primary + alternatives against the period's real bought supply — the same
  // allocation computeUtilizationReport runs — so the dashboard card can't diverge once
  // any restaurant defines alternatives. With none, the engine short-circuits to today's
  // primary-only sum (bit-identical).
  const supply: SupplyMap = new Map(Array.from(ordered.values()).map((p) => [p.key, p.orderedBase]));
  const alloc = allocateNeeds(weightNeedsBySales(args.perUnit, periodRevenue), supply);
  // Latest prices come from the FULL order history (not just the range).
  const sortedAllOrders = [...args.orders].sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
  // Linked regardless of period sales — mirrors computeUtilizationReport's 0%-row rule.
  const linkedKeys = new Set<string>();
  for (const group of [args.perUnit.dishes, args.perUnit.choiceDefaults, args.perUnit.choiceOverrides]) {
    for (const entry of group) for (const n of entry.needs) for (const o of n.options) linkedKeys.add(o.key);
  }

  let periodSpend = 0;
  let totalPotentialWasteEur = 0;
  for (const p of Array.from(ordered.values())) {
    periodSpend += p.spend;
    if (p.side !== 'food' && p.side !== 'drinks') continue;
    if (!p.hasQuantityInfo) continue;
    const neededBase = alloc.get(p.key)?.neededBase ?? (linkedKeys.has(p.key) ? 0 : undefined);
    if (neededBase === undefined) continue; // unmapped product → excluded
    const price = latestPricePerBase(p.key, sortedAllOrders);
    if (price === null) continue;
    const wasteEur = (p.orderedBase - neededBase) * price;
    if (wasteEur > 0) totalPotentialWasteEur += wasteEur; // over-ordering only
  }

  return {
    totalPotentialWasteEur,
    periodSpend,
    wastePercent: periodSpend > 0 ? totalPotentialWasteEur / periodSpend : 0,
  };
}

// --- valuation ------------------------------------------------------------

/** Latest ordered price per base unit for a product key, across ALL order history
 *  (orders pre-sorted newest first). Mirrors server `latestPricePerBase`. */
function latestPricePerBase(key: string, sortedAllOrders: OrderHistory[]): number | null {
  const candidates: EmailProductData[] = [];
  for (const o of sortedAllOrders) {
    for (const p of o.productData) {
      if (productKey(o.platform, p.articleId, p.name) === key) candidates.push(p);
    }
  }
  const chosen = selectValuationLine(candidates, p => p.price);
  return chosen ? pricePerStdUnit(chosen) : null;
}

// --- grouped-view assembly ------------------------------------------------

/** Allocation slices below this are treated as zero (float noise from the engine). */
const ALLOC_EPS = 1e-9;

/** Member line for a configured option with no flat row (not purchased this period):
 *  ordered 0, needed = whatever the engine attributed to it (e.g. shortfall on an
 *  unpurchased primary). Display data comes from the cost-price ingredient rows. */
function configuredOnlyMember(
  key: string,
  isPrimary: boolean,
  neededBase: number,
  nameByKey: Map<string, string>,
  infoByKey: Map<string, NeedProductInfo>
): UtilizationGroupMember {
  const info = infoByKey.get(key);
  const platform = info?.platform ?? key.split('::')[0];
  return {
    key,
    name: nameByKey.get(key) ?? info?.name ?? key,
    platform,
    unknownSupplierName: info?.customPlatformName,
    supplier: info?.customPlatformName ?? platform,
    articleId: info?.articleId,
    stdUnit: info?.stdUnit ?? '',
    isPrimary,
    orderedBase: 0,
    neededBase,
    wasteBase: -neededBase,
    wasteEur: null,
    utilizationPercent: null,
  };
}

/** Group base-unit aggregation: members that move volume must share a known std
 *  unit, else base columns are null (— in the UI; € still sums). Untouched 0/0
 *  members never block unit agreement. */
function aggregateGroupMembers(members: UtilizationGroupMember[]): {
  stdUnit: string | null;
  orderedBase: number | null;
  neededBase: number | null;
  wasteEur: number | null;
} {
  const contributing = members.filter((m) => m.orderedBase > 0 || m.neededBase > ALLOC_EPS);
  const sameUnit =
    contributing.length > 0 && contributing.every((m) => m.stdUnit !== '' && m.stdUnit === contributing[0].stdUnit);
  const stdUnit = sameUnit ? contributing[0].stdUnit : null;
  const eurMembers = members.filter((m) => m.wasteEur !== null);
  return {
    stdUnit,
    orderedBase: stdUnit !== null ? members.reduce((s, m) => s + m.orderedBase, 0) : null,
    neededBase: stdUnit !== null ? members.reduce((s, m) => s + m.neededBase, 0) : null,
    wasteEur: eurMembers.length > 0 ? eurMembers.reduce((s, m) => s + (m.wasteEur as number), 0) : null,
  };
}

/** Assemble the Dish-grouped-tab rows (GROUPS ONLY) from the flat rows + the
 *  engine's per-option-set allocations. Each pooled product's ordered volume is
 *  sliced across its option sets proportionally to the allocation it received from
 *  each set, so a product in several sets is never double-counted. Products with no
 *  multi-product set (or no allocation at all) are not shown here — they live on
 *  the Products tab. */
function buildGroupedRows(
  rows: UtilizationProductRow[],
  needIndex: Map<string, NeededInfo>,
  needs: AllocationNeed[],
  nameByKey: Map<string, string>,
  infoByKey: Map<string, NeedProductInfo>
): UtilizationGroupRow[] {
  // Only sets with ≥2 distinct products form groups; single-option sets are the
  // ordinary no-alternative case and stay solo.
  const setMeta = new Map(
    Array.from(collectOptionSets(needs).entries()).filter(([, m]) => m.keys.length > 1)
  );

  const membersBySig = new Map<string, UtilizationGroupMember[]>();

  for (const r of rows) {
    const bySet = needIndex.get(r.key)?.allocationsBySet;
    const entries = bySet ? Array.from(bySet.entries()) : [];
    const total = entries.reduce((s, [, v]) => s + v, 0);
    if (total <= ALLOC_EPS) continue;
    for (const [sig, alloc] of entries) {
      const meta = setMeta.get(sig);
      if (!meta) continue; // single-key set — non-alternative demand, not shown here
      const orderedSlice = r.orderedBase * (alloc / total);
      const wasteSlice = orderedSlice - alloc;
      const member: UtilizationGroupMember = {
        key: r.key,
        name: r.name,
        platform: r.platform,
        unknownSupplierName: r.unknownSupplierName,
        supplier: r.supplier,
        articleId: r.articleId,
        category: r.category,
        stdUnit: r.stdUnit,
        isPrimary: meta.primaryKey === r.key,
        orderedBase: orderedSlice,
        neededBase: alloc,
        wasteBase: wasteSlice,
        wasteEur: r.pricePerBase !== null ? wasteSlice * r.pricePerBase : null,
        utilizationPercent: orderedSlice > 0 ? (alloc / orderedSlice) * 100 : null,
        row: r,
      };
      const list = membersBySig.get(sig);
      if (list) list.push(member);
      else membersBySig.set(sig, [member]);
    }
  }

  const groups: UtilizationGroupRow[] = [];
  for (const [sig, members] of Array.from(membersBySig.entries())) {
    const meta = setMeta.get(sig);
    if (!meta) continue;
    // Configured members with no flat row (not purchased this period) still show,
    // so the group always lists its full composition.
    const present = new Set(members.map((m) => m.key));
    for (const key of meta.keys) {
      if (present.has(key)) continue;
      const alloc = needIndex.get(key)?.allocationsBySet?.get(sig) ?? 0;
      members.push(configuredOnlyMember(key, meta.primaryKey === key, alloc, nameByKey, infoByKey));
    }
    members.sort(
      (a, b) => Number(b.isPrimary) - Number(a.isPrimary) || (b.wasteEur ?? -Infinity) - (a.wasteEur ?? -Infinity)
    );
    const agg = aggregateGroupMembers(members);
    const primaryMember = members.find((m) => m.isPrimary);
    const rowMember = members.find((m) => m.isPrimary && m.row) ?? members.find((m) => m.row);
    groups.push({
      signature: sig,
      primaryName: nameByKey.get(meta.primaryKey) ?? primaryMember?.name ?? meta.primaryKey,
      configuredCount: meta.keys.length,
      side: rowMember?.row?.side ?? 'food',
      dishes: Array.from(meta.dishes).sort(),
      subrecipes: Array.from(meta.subrecipes).sort(),
      stdUnit: agg.stdUnit,
      orderedBase: agg.orderedBase,
      neededBase: agg.neededBase,
      utilizationPercent:
        agg.orderedBase !== null && agg.orderedBase > 0 && agg.neededBase !== null
          ? (agg.neededBase / agg.orderedBase) * 100
          : null,
      wasteBase: agg.orderedBase !== null && agg.neededBase !== null ? agg.orderedBase - agg.neededBase : null,
      wasteEur: agg.wasteEur,
      members,
    });
  }

  groups.sort((a, b) => (b.wasteEur ?? -Infinity) - (a.wasteEur ?? -Infinity));
  return groups;
}

/** Assemble the Pooled-tab rows (GROUPS ONLY): every product connected through any
 *  alternative link — directly or transitively across dishes — collapses into ONE
 *  pool row, the same union the engine allocates over. Pools partition the flat
 *  rows, so members keep their full (unsliced) numbers. Pools are split by
 *  food/drinks side so the side filter stays honest; sides left with a single
 *  member (and products with no pool) are not shown here — see the Products tab. */
function buildPooledRows(
  rows: UtilizationProductRow[],
  needs: AllocationNeed[],
  nameByKey: Map<string, string>,
  infoByKey: Map<string, NeedProductInfo>,
  needIndex: Map<string, NeededInfo>
): UtilizationGroupRow[] {
  const pools = new Map<string, string[]>();
  const poolByKey = new Map<string, { poolKey: string; size: number }>();
  for (const [poolKey, memberKeys] of Array.from(collectPools(needs).entries())) {
    if (memberKeys.length < 2) continue;
    pools.set(poolKey, memberKeys);
    for (const k of memberKeys) poolByKey.set(k, { poolKey, size: memberKeys.length });
  }
  // Keys appearing as a need's primary (options[0]) — anchor-name candidates.
  const primaryKeys = new Set<string>();
  for (const n of needs) if (n.options.length > 0) primaryKeys.add(n.options[0].key);

  // Partition each pool's PURCHASED rows by food/drinks side.
  const rowsByPartition = new Map<string, UtilizationProductRow[]>();
  const partitionInfo = new Map<string, { poolKey: string; side: 'food' | 'drinks'; size: number }>();
  const partitionsByPool = new Map<string, string[]>();
  for (const r of rows) {
    const pool = poolByKey.get(r.key);
    if (!pool) continue;
    const sig = `${pool.poolKey}${r.side}`;
    const list = rowsByPartition.get(sig);
    if (list) list.push(r);
    else {
      rowsByPartition.set(sig, [r]);
      partitionInfo.set(sig, { poolKey: pool.poolKey, side: r.side, size: pool.size });
      const sigs = partitionsByPool.get(pool.poolKey);
      if (sigs) sigs.push(sig);
      else partitionsByPool.set(pool.poolKey, [sig]);
    }
  }

  // Configured pool members with no flat row (not purchased this period) attach to
  // the pool's largest side-partition, so the group shows its full composition.
  const extrasBySig = new Map<string, UtilizationGroupMember[]>();
  for (const [poolKey, memberKeys] of Array.from(pools.entries())) {
    const sigs = partitionsByPool.get(poolKey);
    if (!sigs || sigs.length === 0) continue; // nothing purchased → pool not shown
    const purchased = new Set(sigs.flatMap((sig) => (rowsByPartition.get(sig) ?? []).map((r) => r.key)));
    const target = [...sigs].sort(
      (a, b) => (rowsByPartition.get(b)?.length ?? 0) - (rowsByPartition.get(a)?.length ?? 0) || a.localeCompare(b)
    )[0];
    for (const key of memberKeys) {
      if (purchased.has(key)) continue;
      const member = configuredOnlyMember(key, false, needIndex.get(key)?.base ?? 0, nameByKey, infoByKey);
      const list = extrasBySig.get(target);
      if (list) list.push(member);
      else extrasBySig.set(target, [member]);
    }
  }

  const groups: UtilizationGroupRow[] = [];
  for (const [sig, rowMembers] of Array.from(rowsByPartition.entries())) {
    const info = partitionInfo.get(sig);
    if (!info) continue;
    const extras = extrasBySig.get(sig) ?? [];
    if (rowMembers.length + extras.length < 2) continue;
    // Anchor: the most-needed purchased member that is a primary somewhere
    // (fallback: the most-needed member), tie-broken by key for determinism.
    const byNeed = [...rowMembers].sort((a, b) => b.neededBase - a.neededBase || a.key.localeCompare(b.key));
    const anchor = byNeed.find((m) => primaryKeys.has(m.key)) ?? byNeed[0];
    const members: UtilizationGroupMember[] = rowMembers
      .map((m): UtilizationGroupMember => ({
        key: m.key,
        name: m.name,
        platform: m.platform,
        unknownSupplierName: m.unknownSupplierName,
        supplier: m.supplier,
        articleId: m.articleId,
        category: m.category,
        stdUnit: m.stdUnit,
        isPrimary: m.key === anchor.key,
        orderedBase: m.orderedBase,
        neededBase: m.neededBase,
        wasteBase: m.wasteBase,
        wasteEur: m.wasteEur,
        utilizationPercent: m.utilizationPercent,
        row: m,
      }))
      .concat(extras)
      .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || (b.wasteEur ?? -Infinity) - (a.wasteEur ?? -Infinity));
    const agg = aggregateGroupMembers(members);
    const dishes = new Set(rowMembers.flatMap((m) => m.dishes));
    const subrecipes = new Set(rowMembers.flatMap((m) => m.subrecipes));
    for (const e of extras) {
      const need = needIndex.get(e.key);
      for (const d of Array.from(need?.dishes ?? [])) dishes.add(d);
      for (const s of Array.from(need?.subrecipes ?? [])) subrecipes.add(s);
    }
    groups.push({
      signature: sig,
      primaryName: nameByKey.get(anchor.key) ?? anchor.name,
      configuredCount: info.size,
      side: info.side,
      dishes: Array.from(dishes).sort(),
      subrecipes: Array.from(subrecipes).sort(),
      stdUnit: agg.stdUnit,
      orderedBase: agg.orderedBase,
      neededBase: agg.neededBase,
      utilizationPercent:
        agg.orderedBase !== null && agg.orderedBase > 0 && agg.neededBase !== null
          ? (agg.neededBase / agg.orderedBase) * 100
          : null,
      wasteBase: agg.orderedBase !== null && agg.neededBase !== null ? agg.orderedBase - agg.neededBase : null,
      wasteEur: agg.wasteEur,
      members,
    });
  }

  groups.sort((a, b) => (b.wasteEur ?? -Infinity) - (a.wasteEur ?? -Infinity));
  return groups;
}

// --- entry point ----------------------------------------------------------

export function computeUtilizationReport(inputs: UtilizationReportInputs): UtilizationReportData {
  const {
    orders,
    revenue,
    costPriceList,
    subrecipesById,
    defaultWastePercentage,
    currentRestaurantId,
    range,
  } = inputs;

  const hasMappableCostPrices = costPriceList.some(
    (c) => c.costPriceType !== COST_PRICE_TYPE_CHOICE && !!c.menuItemName
  );

  const periodOrders = orders.filter((o) => inRange(new Date(o.orderDate), range));
  const periodRevenue = revenue.filter((r) => inRange(new Date(r.date), range));

  const ordered = aggregateOrderedProducts(periodOrders);
  // Interchangeable-pool supply: base units ordered per product key in the period.
  const supply: SupplyMap = new Map(Array.from(ordered.values()).map((p) => [p.key, p.orderedBase]));
  // Key → display name, to render "alternative for <primary>" badges on pooled rows.
  // Purchased names are authoritative; fill any option product not bought this period
  // (e.g. a primary whose demand all landed on its alternative) from the needs side so
  // the badge shows a product name rather than the raw resolver key.
  const nameByKey = new Map(Array.from(ordered.values()).map((p) => [p.key, p.name]));
  const needProductInfo = collectNeedProductInfo(costPriceList, subrecipesById);
  for (const [key, info] of Array.from(needProductInfo.entries())) {
    if (!nameByKey.has(key)) nameByKey.set(key, info.name);
  }
  // The collected needs feed the grouped view's option-set metadata (group members,
  // primary anchor, dish labels) — the allocation itself is unchanged.
  const collectedNeeds: AllocationNeed[] = [];
  const analysisIdByDish = new Map<string, string>();
  const unitsSoldByDish = new Map<string, number>();
  const needIndex = buildNeededIndex(
    costPriceList,
    periodRevenue,
    subrecipesById,
    defaultWastePercentage,
    currentRestaurantId,
    productKey,
    supply,
    collectedNeeds,
    analysisIdByDish,
    unitsSoldByDish
  );

  // Latest prices come from the FULL order history (not just the range).
  const sortedAllOrders = [...orders].sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());

  // Last orders per period product (all-time, newest first) for the drawer's links.
  const recentByKey = new Map<string, RecentProductOrder[]>();
  for (const o of sortedAllOrders) {
    for (const p of o.productData) {
      const key = productKey(o.platform, p.articleId, p.name);
      if (!ordered.has(key)) continue;
      let list = recentByKey.get(key);
      if (!list) {
        list = [];
        recentByKey.set(key, list);
      }
      if (list.length >= RECENT_ORDERS_LIMIT || list.some((e) => e.orderHistoryId === o.id)) continue;
      list.push({
        orderHistoryId: o.id,
        orderDate: o.orderDate,
        quantity: getProductQuantity(p, o.platform),
        unitPrice: p.price ?? 0,
      });
    }
  }

  const rows: UtilizationProductRow[] = [];
  const unmappedFood: UtilizationGroup = { count: 0, spend: 0 };
  const unmappedDrinks: UtilizationGroup = { count: 0, spend: 0 };
  const unmappedRows: UnmappedProductRow[] = [];
  const noQtyFood: UtilizationGroup = { count: 0, spend: 0 };
  const noQtyDrinks: UtilizationGroup = { count: 0, spend: 0 };
  let periodSpend = 0;
  let spendFood = 0;
  let spendDrinks = 0;

  for (const p of Array.from(ordered.values())) {
    periodSpend += p.spend;
    if (p.side === 'food') spendFood += p.spend;
    else if (p.side === 'drinks') spendDrinks += p.spend;
    // Only food/drinks sides are reported (non-food orders are excluded, like the export).
    if (p.side !== 'food' && p.side !== 'drinks') continue;

    if (!p.hasQuantityInfo) {
      const g = p.side === 'food' ? noQtyFood : noQtyDrinks;
      g.count += 1;
      g.spend += p.spend;
      continue;
    }

    const need = needIndex.get(p.key);
    // "Not linked" = in NO cost price / subrecipe at all. A linked product whose dishes
    // simply didn't sell this period is a 0%-utilization row, not a setup gap.
    if ((!need || need.dishes.size === 0) && !needProductInfo.has(p.key)) {
      const g = p.side === 'food' ? unmappedFood : unmappedDrinks;
      g.count += 1;
      g.spend += p.spend;
      unmappedRows.push({
        key: p.key,
        name: p.name,
        supplier: p.supplier,
        platform: p.platform,
        unknownSupplierName: p.unknownSupplierName,
        articleId: p.articleId,
        side: p.side,
        category: p.category,
        spend: p.spend,
      });
      continue;
    }

    const neededBase = need?.base ?? 0;
    const wasteBase = p.orderedBase - neededBase;
    const price = latestPricePerBase(p.key, sortedAllOrders);
    rows.push({
      key: p.key,
      name: p.name,
      supplier: p.supplier,
      platform: p.platform,
      unknownSupplierName: p.unknownSupplierName,
      articleId: p.articleId,
      side: p.side,
      category: p.category,
      dishes: Array.from(need?.dishes ?? []).sort(),
      subrecipes: Array.from(need?.subrecipes ?? []).sort(),
      spend: p.spend,
      stdUnit: p.stdUnit,
      orderedBase: p.orderedBase,
      neededBase,
      utilizationPercent: p.orderedBase > 0 ? (neededBase / p.orderedBase) * 100 : null,
      wasteBase,
      wasteEur: price !== null ? wasteBase * price : null,
      pricePerBase: price,
      recentOrders: recentByKey.get(p.key) ?? [],
      receivedViaAlternative: need?.receivedViaAlternative ?? false,
      alternativeForNames: Array.from(need?.alternativeForKeys ?? [])
        .map((k) => nameByKey.get(k) ?? k)
        .sort(),
      dishesViaAlternative: Array.from(need?.dishesViaAlternative ?? []).sort(),
      dishBreakdown: buildProductDishBreakdown(p.key, collectedNeeds, price, need?.dishes ?? new Set<string>(), analysisIdByDish, unitsSoldByDish),
    });
  }

  rows.sort((a, b) => (b.wasteEur ?? -Infinity) - (a.wasteEur ?? -Infinity));
  unmappedRows.sort((a, b) => b.spend - a.spend);

  const grouped = buildGroupedRows(rows, needIndex, collectedNeeds, nameByKey, needProductInfo);
  const pooled = buildPooledRows(rows, collectedNeeds, nameByKey, needProductInfo, needIndex);

  const totalPotentialWasteEur = rows.reduce(
    (sum, r) => sum + (r.wasteEur !== null && r.wasteEur > 0 ? r.wasteEur : 0),
    0
  );
  const overOrderedCount = rows.filter((r) => r.wasteBase > 0).length;

  const suppliers = Array.from(new Set(rows.map((r) => r.supplier))).sort();
  const categories = Array.from(new Set(rows.map((r) => r.category).filter((c): c is string => !!c))).sort();
  const dishNames = Array.from(new Set(rows.flatMap((r) => r.dishes))).sort();
  const subrecipeNames = Array.from(new Set(rows.flatMap((r) => r.subrecipes))).sort();

  return {
    hasMappableCostPrices,
    rows,
    grouped,
    pooled,
    unmappedFood,
    unmappedDrinks,
    unmappedRows,
    noQuantityInfoFood: noQtyFood,
    noQuantityInfoDrinks: noQtyDrinks,
    totalPotentialWasteEur,
    periodSpend,
    spendBySide: { food: spendFood, drinks: spendDrinks },
    wastePercentOfSpend: periodSpend > 0 ? (totalPotentialWasteEur / periodSpend) * 100 : 0,
    overOrderedCount,
    suppliers,
    categories,
    dishNames,
    subrecipeNames,
    comparisonHidden: range.comparisonHidden,
  };
}
